"use client";

import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";

const CACHE_KEY = "publicSettingsCache";

interface PublicSettings {
  whatsAppButtonEnabled: boolean;
}

const DEFAULTS: PublicSettings = { whatsAppButtonEnabled: true };

function readCache(): PublicSettings | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as PublicSettings) : null;
  } catch {
    return null;
  }
}

/**
 * The one business-settings field the *public* site itself needs to read (whether the floating
 * WhatsApp button shows at all) -- everything else in settings/business only ever matters
 * server-side (booking rules, deposit %, etc.), so this deliberately reads just this one field
 * rather than the whole document. Same read-once-cache-for-the-session shape as
 * useSiteNavigation/useSiteBlocks: defaults to "on" (today's actual behavior) so nothing changes
 * for a visitor unless an admin explicitly turns it off.
 */
export function usePublicSettings(): PublicSettings {
  const [settings, setSettings] = useState<PublicSettings>(
    () => (typeof window !== "undefined" && readCache()) || DEFAULTS
  );

  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, "settings", "business"))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.data();
        const next: PublicSettings = {
          whatsAppButtonEnabled:
            typeof data?.whatsAppButtonEnabled === "boolean" ? data.whatsAppButtonEnabled : true,
        };
        setSettings(next);
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(next));
        } catch {
          // Private browsing / storage disabled -- the fetch itself still succeeded.
        }
      })
      .catch(() => {
        // Whatever's already rendered (cache or the "on" default) stands.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return settings;
}

export function clearPublicSettingsCache(): void {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // Nothing to clear.
  }
}
